import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Button,
} from "react-bootstrap";
import { useGetApplicationByIdQuery } from "../../../redux/api/CareersApi/careersApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import {
  FaArrowLeft,
  FaFilePdf,
  FaLinkedin,
  FaExternalLinkAlt,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: app, isLoading } = useGetApplicationByIdQuery(id);

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  if (!app) return <div>Application not found</div>;

  return (
    <Container className="py-4">
      <div className="d-flex align-items-center gap-3 mb-4">
        <Button
          variant="light"
          onClick={() => navigate("/admin/applications")}
          className="rounded-circle shadow-sm p-2"
        >
          <FaArrowLeft />
        </Button>
        <h2 className="fw-bold text-dark mb-0">Candidate Details</h2>
        <Badge bg="primary" className="ms-auto px-3 py-2 rounded-pill">
          {app.status}
        </Badge>
      </div>

      <Row className="g-4">
        <Col lg={4}>
          <Card className="border-0 shadow-sm rounded-4 p-4 text-center mb-4">
            <div
              className="bg-light rounded-circle p-4 m-auto mb-3"
              style={{
                width: "100px",
                height: "100px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <h2 className="mb-0 text-primary">{app.fullName.charAt(0)}</h2>
            </div>
            <h4 className="fw-bold mb-1">{app.fullName}</h4>
            <p className="text-muted mb-4">{app.job?.title}</p>

            <div className="d-grid gap-2">
              <Button
                variant="outline-primary"
                as="a"
                href={app.resumePath}
                target="_blank"
                className="rounded-pill"
              >
                <FaFilePdf className="me-2" /> View Resume
              </Button>
            </div>
          </Card>

          <Card className="border-0 shadow-sm rounded-4 p-4">
            <h5 className="fw-bold mb-3">Contact Information</h5>
            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="bg-light p-2 rounded-circle text-primary">
                <FaEnvelope />
              </div>
              <div>
                <small className="text-muted d-block">Email</small>
                <span>{app.email}</span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 mb-3">
              <div className="bg-light p-2 rounded-circle text-primary">
                <FaPhone />
              </div>
              <div>
                <small className="text-muted d-block">Phone</small>
                <span>{app.phone}</span>
              </div>
            </div>
            {app.linkedinUrl && (
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="bg-light p-2 rounded-circle text-primary">
                  <FaLinkedin />
                </div>
                <div>
                  <small className="text-muted d-block">LinkedIn</small>
                  <a
                    href={app.linkedinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-none"
                  >
                    Profile <FaExternalLinkAlt size={10} />
                  </a>
                </div>
              </div>
            )}
            {app.portfolioUrl && (
              <div className="d-flex align-items-center gap-3">
                <div className="bg-light p-2 rounded-circle text-primary">
                  <FaExternalLinkAlt />
                </div>
                <div>
                  <small className="text-muted d-block">
                    Portfolio / Website
                  </small>
                  <a
                    href={app.portfolioUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-decoration-none"
                  >
                    View Portfolio
                  </a>
                </div>
              </div>
            )}
          </Card>
        </Col>

        <Col lg={8}>
          <Card className="border-0 shadow-sm rounded-4 p-4 mb-4">
            <h5 className="fw-bold mb-4">Professional Details</h5>
            <Row className="g-3">
              <Col md={6}>
                <div className="bg-light p-3 rounded-4">
                  <small
                    className="text-muted d-block text-uppercase fw-bold"
                    style={{ fontSize: "10px" }}
                  >
                    Total Experience
                  </small>
                  <h5 className="mb-0">{app.totalExperience} Years</h5>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-4">
                  <small
                    className="text-muted d-block text-uppercase fw-bold"
                    style={{ fontSize: "10px" }}
                  >
                    Notice Period
                  </small>
                  <h5 className="mb-0">{app.noticePeriod}</h5>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-4">
                  <small
                    className="text-muted d-block text-uppercase fw-bold"
                    style={{ fontSize: "10px" }}
                  >
                    Current CTC
                  </small>
                  <h5 className="mb-0">{app.currentCTC}</h5>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-4">
                  <small
                    className="text-muted d-block text-uppercase fw-bold"
                    style={{ fontSize: "10px" }}
                  >
                    Expected CTC
                  </small>
                  <h5 className="mb-0">{app.expectedCTC}</h5>
                </div>
              </Col>
            </Row>
          </Card>

          {app.coverLetter && (
            <Card className="border-0 shadow-sm rounded-4 p-4">
              <h5 className="fw-bold mb-4">Cover Letter / Message</h5>
              <div
                className="bg-light p-4 rounded-4"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {app.coverLetter}
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ApplicationDetail;
